import React from 'react';
import { Box, Paper, Typography, Button, Stack, Alert } from '@mui/material';
import { LockOutlined as LockIcon } from '@mui/icons-material';
import { useAuth } from 'shared/contexts/AuthContext';
import { RelationshipVerificationsCard } from './RelationshipVerificationsCard';

interface Props {
  relationshipId: string;
  companyName?: string;
}

/**
 * Landing screen for a provider's home company_admin while logged into a
 * client company whose blockUnverifiedAccess requirements aren't met yet.
 * They're let in (see getBlockedExternalCompanyIds's isHomeCompanyAdminOf
 * exemption) specifically so they can land here instead of the normal app —
 * this is the only thing they can do until the client's required documents
 * are approved.
 */
export const RestrictedProviderAccessScreen: React.FC<Props> = ({ relationshipId, companyName }) => {
  const { logout, user } = useAuth();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: { xs: 2, md: 4 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 900,
          p: { xs: 2, md: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mt: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
          <LockIcon color="warning" sx={{ mt: 0.25 }} />
          <Box>
            <Typography variant="h6" component="h1">
              Acceso restringido{companyName ? ` a ${companyName}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.company?.name ? `${user.company.name} tiene` : 'Su empresa tiene'} documentos
              pendientes por completar para esta empresa cliente. Mientras no se aprueben, solo puede
              enviar y revisar el estado de esos documentos aquí.
            </Typography>
          </Box>
        </Stack>

        <Alert severity="info" sx={{ mb: 3 }}>
          Una vez que el cliente apruebe los documentos requeridos, su acceso normal se restaurará
          automáticamente.
        </Alert>

        <RelationshipVerificationsCard relationshipId={relationshipId} mode="provider" />

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={logout}>
            Cerrar sesión
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
